import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useTable, usePagination } from "react-table";
import ReactPaginate from "react-paginate";
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from "react-icons/md";
import { useSearchParams } from "react-router-dom";

// Internal components
import ResponseLoader from "../../components/Loader/ResponseLoader";
import AdminDeleteModal from "./Modal/AdminDeleteModal";
import AdminNavbar from "./Layout/AdminNavbar";
import AdminInfoBox from "./Layout/AdminInfoBox";
import { useTranslation } from "react-i18next";
import { getItemToken } from "../../Utils/localStorageUtils";
import { capsFirstLetter } from "../Pages/CommonMethods/CommonMethods";
import { ActionFormatterComponent, statusFormatter } from "../Pages/CommonMethods/CommonBoostrapFormatter";

function AdvertisementList() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [apiData, setApiData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [refreshToggle, setRefreshToggle] = useState(false);
    const [infoBox, setInfoBox] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const userAccessToken = getItemToken("access_token");

    const fetchData = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_Baseurl}/advertiser/advertisement`, {
                headers: userAccessToken,
            });
            const result = response?.data?.advertisements || [];
            const updatedList = result.map((item) => ({
                ...item,
                ads_category: capsFirstLetter(item?.ads_category_name || ""),
                ads_position: capsFirstLetter(item?.ads_position || ""),
                location: capsFirstLetter(item?.location || ""),
                status: item?.status === 1 ? "Published" : "Unpublished",
            }));
            setApiData(updatedList);
            setFilteredData(updatedList);
        } catch (error) {
            setApiData([]);
            setFilteredData([]);
            const response = error?.response;
            if (response?.status === 403) {
                setErrorMessage(response?.data?.message);
                setInfoBox(true);
            }
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.clear();
        setInfoBox(false);
        window.location.href = "/login";
    };

    const handleCheckboxChange = (id) => {
        const updated = selectedItems.includes(id)
            ? selectedItems.filter((i) => i !== id)
            : [...selectedItems, id];
        setSelectedItems(updated);
        setSelectAll(updated.length === page.length);
    };

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        if (newSelectAll) {
            const currentPageIds = page.map((row) => row.original.id);
            setSelectedItems(currentPageIds);
        } else {
            setSelectedItems([]);
        }
    };

    const singleDelete = (id) => {
        setShowDeleteModal(true);
        setSelectedItems([id]);
    };

    const handleConfirmDelete = async () => {
        try {
            for (const id of selectedItems) {
                await axios.delete(`${process.env.REACT_APP_Baseurl}/advertiser/advertisement-delete/${id}`, {
                    headers: userAccessToken,
                });
            }
            toast.success("Deleted successfully!");
            setRefreshToggle(!refreshToggle);
            setSelectedItems([]);
            setSelectAll(false);
        } catch (error) {
            toast.error("Delete failed.");
        } finally {
            setShowDeleteModal(false);
        }
    };

    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                    />
                ),
                accessor: "select",
                Cell: ({ row }) => (
                    <input
                        type="checkbox"
                        checked={selectedItems.includes(row.original.id)}
                        onChange={() => handleCheckboxChange(row.original.id)}
                    />
                ),
                disableSortBy: true,
            },
            {
                Header: "Ads Name",
                accessor: "ads_name",
                Cell: ({ row }) => (
                    <div>
                        <div className="">{row.original.ads_name}</div>
                        <div className="text-muted small">Ads ID: {row.original.ad_unique_id}</div>
                    </div>
                ),
            },

            { Header: "Ads Category", accessor: "ads_category" },
            { Header: "Ads Position", accessor: "ads_position" },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value }) => statusFormatter(value),
            },
            {
                Header: "Actions",
                accessor: "ACTIONS",
                Cell: ({ row }) => (
                    <ActionFormatterComponent
                        row={row}
                        link={`/ads-edit/${row.original.id}`}
                        action={() => singleDelete(row.original.id)}
                    />
                ),
            },
        ],
        [selectAll, selectedItems]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        setPageSize: setTablePageSize,
        pageCount,
        state: { pageIndex }
    } = useTable(
        {
            columns,
            data: filteredData,
            initialState: { pageIndex: 0, pageSize: rowsPerPage }
        },
        usePagination
    );

    const handlePageClick = ({ selected }) => {
        gotoPage(selected);
        setSelectedItems([]);
        setSelectAll(false);
    };

    const handleRowsPerPageChange = (e) => {
        const value = e.target.value;
        const size = value === "All" ? filteredData?.length : parseInt(value);
        setRowsPerPage(size);
        setTablePageSize(size);
        setSelectedItems([]);
        setSelectAll(false);
    };

    useEffect(() => {
        fetchData();
        
        // Handle payment success redirects
        const success = searchParams.get('success');
        const plan = searchParams.get('plan');
        const gateway = searchParams.get('gateway');
        
        if (success === 'payment_completed') {
            const planName = plan ? decodeURIComponent(plan) : 'your plan';
            toast.success(` Payment successful! Welcome to ${planName}! Your subscription is now active.`);
            // Clear URL parameters
            setSearchParams({});
        } else if (success === 'already_exists') {
            const planName = plan ? decodeURIComponent(plan) : 'your plan';
            toast.info(`You already have an active subscription for ${planName}`);
            // Clear URL parameters
            setSearchParams({});
        }
    }, [refreshToggle, searchParams, setSearchParams]);

    return (
        <>
            {infoBox && (
                <AdminInfoBox
                    modelClose={() => setInfoBox(false)}
                    hideCloseBtn
                    closeAction={() => setInfoBox(false)}
                    confirmAction={logout}
                    confirmText="Login"
                    heading={errorMessage}
                    message="Please login again. Your token has expired."
                />
            )}

            {showDeleteModal && (
                <AdminDeleteModal
                    modelClose={showDeleteModal}
                    deleteClose={() => setShowDeleteModal(false)}
                    deleteAction={handleConfirmDelete}
                />
            )}

            {loading && <ResponseLoader />}

            <div className="theme-bg-color p-3 rounded-2 bootstrapTable multi-delete-width">
                <AdminNavbar
                    heading="All Advertisements"
                    totalDataLength={apiData?.length || ""}
                    deleteCount={selectedItems?.length}
                    navigation="/ads-create"
                    deleteMethod={() => setShowDeleteModal(true)}
                    navText="Add"
                />




                <table {...getTableProps()} className="no-border-table theme-text-color w-100">
                    <thead style={{ borderBottom: "2px solid #e3e3e3" }}>
                        {headerGroups?.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()} >
                        {page?.length > 0 ? (
                            page?.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell) => (
                                            <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                        ))}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={columns?.length} style={{ textAlign: "center", padding: "1rem" }}>
                                    No Advertisement List To Show
                                </td>
                            </tr>
                        )}
                    </tbody>

                </table>

                <div className="d-flex justify-content-between mt-3">

                    <div className="d-flex justify-content-end mb-2 align-items-center">
                        <select
                            className="form-select w-auto"
                            value={rowsPerPage >= filteredData?.length ? "All" : rowsPerPage}
                            onChange={handleRowsPerPageChange}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value="All">All</option>
                        </select>

                        <label className="me-2 ms-2">
                            Showing rows 1 to{" "}
                            {rowsPerPage >= filteredData?.length ? filteredData?.length : rowsPerPage}
                        </label>
                    </div>

                    <ReactPaginate
                        previousLabel={<MdKeyboardDoubleArrowLeft />}
                        nextLabel={<MdKeyboardDoubleArrowRight />}
                        pageCount={pageCount}
                        onPageChange={handlePageClick}
                        forcePage={pageIndex}
                        containerClassName="pagination"
                        activeClassName="active"
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        previousClassName="page-link"
                        nextClassName="page-link"
                    />



                </div>
            </div>
        </>
    );
}

export default AdvertisementList;
